import { useEffect, useMemo, useState, useCallback } from 'react';
import { Typography, Box, IconButton, Button, FormControl, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import dayjs from "dayjs";
import { ArrowBack } from '@mui/icons-material';
import CustomIconButton from 'packages/novous_web/src/common/components/CustomIconButton';
import eyeIcon from 'packages/novous_web/src/assets/icons/Eye.svg'
import InputField from 'packages/novous_web/src/common/components/InputField';
import searchIcon from 'packages/novous_web/src/assets/icons/search.svg'
import useAxios from 'packages/novous_web/src/api/useAxios';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import deleteIcon from 'packages/novous_web/src/assets/icons/Delete.svg'
import Buttons from 'packages/novous_web/src/common/components/Button'
import CustomReportPreview from './CustomReportPreview';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import AlertToast from 'packages/novous_web/src/common/components/Alert';
import useApiError from 'packages/novous_web/src/api/useApiError';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import dragIcon from 'packages/novous_web/src/assets/icons/DragIcon.svg'
import DOMPurify from 'dompurify';


interface DateFieldProps {
    label: string;
    name: string;
    value: string | null;
    onChange: (value: string | null) => void;
    isDisabled?: boolean;
    id: string;
}

interface EmailApiResponse {
    data: string;
    error: string;
    message: string;
    status: number;
}

const DateField: React.FC<DateFieldProps> = ({
    label,
    name,
    value,
    onChange,
    isDisabled = false,
}) => {
    return (
        <div className="flex flex-col w-full">
            <Typography variant="body2" fontWeight={600} color="textPrimary">
                {label}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                    value={value ? dayjs(value, 'DD/MM/YYYY') : null}
                    onChange={(newValue) =>
                        onChange(newValue ? dayjs(newValue).format('DD/MM/YYYY') : null)
                    }
                    format="DD/MM/YYYY"
                    disabled={isDisabled}
                    slotProps={{
                        textField: { fullWidth: true, variant: 'outlined' },
                    }}
                    sx={(theme) => ({
                        width: '100%',
                        marginTop: '8px',
                        '& .MuiOutlinedInput-root': {
                            padding: '12px 16px',
                            fontSize: '14px',
                            fontWeight: 400,
                            borderRadius: '8px',
                            borderWidth: '1px',
                            borderColor: theme.palette.action.selected,
                        },
                        '& .MuiInputBase-input': {
                            padding: '0px',
                            borderWidth: '0px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                        '& .MuiSvgIcon-root': {
                            color: theme.palette.primary.main,
                        },
                    })}
                    className={`mt-2 w-full ${isDisabled ? 'bg-gray-200 cursor-not-allowed' : ''
                        }`}
                />
            </LocalizationProvider>
        </div>
    );
};

interface MasterReport {
    reportWiseColumns: {
        All?: any[];
        all?: any[];
    };
    date_time: string;
    reportName: string;
    reportDescription: string;
    reportDisplayName: string;
    reportType: string;
}

const data: MasterReport[] = []

export default function EditCustomReports() {
    const [alertOpen, setAlertOpen] = useState<boolean>(false);
    const [alertMessage, setAlertMessage] = useState<string>("");
    const [alertType, setAlertType] = useState<"error" | "success" | "warning" | "info">("error");
    const [isSendingEmail, setIsSendingEmail] = useState(false);

    const [reportName, setReportName] = useState('');
    const [reportDescription, setReportDescription] = useState('');
    const [isDateRangeValid, setIsDateRangeValid] = useState(true);
    const [initialDataLoaded, setInitialDataLoaded] = useState(false);
    const [showBackConfirmation, setShowBackConfirmation] = useState(false);
    const [isFormModified, setIsFormModified] = useState(false);
    const [selectedColumnsOrder, setSelectedColumnsOrder] = useState<string[]>([]);

    const navigate = useNavigate();
    const dateRangeArr = [
        "7 Days",
        "15 Days",
        "30 Days",
        "Custom Range",
    ];

    const [selectedDuration, setSelectedDuration] = useState(dateRangeArr[0]);
    const [calendarAnchorEl, setCalendarAnchorEl] = useState<null | HTMLElement>(
        null
    );
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [searchTerm, setSearchTerm] = useState('');

    const getDateRange = (range: string) => {
        const today = dayjs();
        let startDate, endDate;

        switch (range) {
            case "Last 7 Days":
                startDate = dayjs().subtract(6, "day").format("YYYY-MM-DD");
                endDate = today.format("YYYY-MM-DD");
                break;
            case "Last 15 Days":
                startDate = dayjs().subtract(14, "day").format("YYYY-MM-DD");
                endDate = today.format("YYYY-MM-DD");
                break;
            case "Last 30 Days":
                startDate = dayjs().subtract(29, "day").format("YYYY-MM-DD");
                endDate = today.format("YYYY-MM-DD");
                break;
            case "Custom Range":
                // These will be set by the custom inputs
                break;
            default:
                startDate = "";
                endDate = "";
        }
        return { startDate, endDate };
    };

    let { startDate: startDateC, endDate: endDateC } =
        getDateRange(selectedDuration);

    useEffect(() => {
        if (startDateC !== "" && endDateC !== "") {
        }
    }, [startDateC, endDateC]);

    if (startDate !== "" && endDate !== "") {
        startDateC = startDate;
        endDateC = endDate;
    } else if (startDate === "" && endDate === "") {
        ({ startDate: startDateC, endDate: endDateC } =
            getDateRange(selectedDuration));
    }
    useEffect(() => {
        const { startDate: newStartDate = '', endDate: newEndDate = '' } = getDateRange(selectedDuration);
        setStartDate(newStartDate);
        setEndDate(newEndDate);
    }, [selectedDuration]);

    const { report_id, date_time } = useParams<{ report_id: string, date_time: string }>();

    interface CustomReportResponse {
        data: {
            report_id: string;
            date_time: string;
            startDate: string;
            endDate: string;
            fleet_id: string;
            parent_id: string;
            vehicle_group: string | null;
            reportCategory: string;
            reportName: string;
            reportDescription: string;
            selectedColumns: string[];
            createdAt: string;
            updatedAt: string | null;
        };
        message: string;
        status: number;
    }

    const {
        data: CustomReportData,
        error: CustomReportError,
        loading: CustomReportLoading,
        fetchData: CustomReportRequestData,
    } = useAxios<CustomReportResponse>(API_ENDPOINTS.report.CUSTOM_REPORT_DATA_SINGLE, "POST");

    useEffect(() => {
        CustomReportRequestData({
            reportId: report_id,
            dateTime: date_time
        });
    }, [])
    const [selectedColumns, setSelectedColumns] = useState<Record<string, boolean>>({});

    const checkFormModified = useCallback(() => {
        if (!CustomReportData?.data) return false;

        const originalData = CustomReportData.data;
        const currentSelectedColumns = Object.keys(selectedColumns).filter(key => selectedColumns[key]);

        return (
            reportName !== originalData.reportName ||
            reportDescription !== originalData.reportDescription ||
            JSON.stringify(currentSelectedColumns) !== JSON.stringify(originalData.selectedColumns) ||
            startDate !== originalData.startDate ||
            endDate !== originalData.endDate
        );
    }, [CustomReportData, reportName, reportDescription, selectedColumns, startDate, endDate]);

    useEffect(() => {
        setIsFormModified(checkFormModified());
    }, [checkFormModified]);

    const handleBackClick = () => {
        if (isFormModified) {
            setShowBackConfirmation(true);
        } else {
            navigate(-1);
        }
    };

    // Load initial data when CustomReportData is available
    useEffect(() => {
        if (CustomReportData?.data && !initialDataLoaded) {
            const reportData = CustomReportData.data;

            // Set basic report info
            setReportName(reportData.reportName);
            setReportDescription(reportData.reportDescription);

            // Set date range
            if (reportData.startDate && reportData.endDate) {
                setStartDate(reportData.startDate);
                setEndDate(reportData.endDate);

                // Determine which duration option matches these dates
                const today = dayjs();
                const start = dayjs(reportData.startDate);
                const end = dayjs(reportData.endDate);

                const diffDays = end.diff(start, 'day') + 1;

                if (diffDays === 7) {
                    setSelectedDuration("Last 7 Days");
                } else if (diffDays === 15) {
                    setSelectedDuration("Last 15 Days");
                } else if (diffDays === 30) {
                    setSelectedDuration("Last 30 Days");
                } else {
                    setSelectedDuration("Custom Range");
                }
            }

            if (CustomReportData.data.selectedColumns) {
                setSelectedColumnsOrder(CustomReportData.data.selectedColumns);
            }

            setInitialDataLoaded(true);
        }
    }, [CustomReportData, initialDataLoaded]);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
        setCalendarAnchorEl(event.currentTarget);
    };

    const handleCalendarClose = () => {
        setShowDatePicker(false);
        setStartDate("");
        setEndDate("");

        setSelectedDuration(dateRangeArr[0]);
        setCalendarAnchorEl(null);
    };

    const handleClose = (value?: string) => {
        console.log("Selected Value:", value);

        if (value) {
            setSelectedDuration(value);
            setShowDatePicker(value === "Custom Range");

            const { startDate: newStartDate = '', endDate: newEndDate = '' } = getDateRange(selectedDuration);
            setStartDate(newStartDate);
            setEndDate(newEndDate);
        }

        setAnchorEl(null);
    };

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { user } = useAuth();
    const [masterReports, setMasterReports] = useState<MasterReport[]>([]);

    const {
        data: MasterReportData,
        error: MasterReportError,
        loading: MasterReportLoading,
        fetchData: MasterReportRequestData,
    }: { data: any, error: any, loading: boolean, fetchData: any } = useAxios(API_ENDPOINTS.report.MASTER_REPORTS, "POST");

    useEffect(() => {
        MasterReportRequestData({
            fleetId: user.customer_id,
        });
    }, [])

    useEffect(() => {
        if (MasterReportData && MasterReportData.data) {
            setMasterReports(MasterReportData.data);

            // If we have custom report data, find and select the matching master report
            if (CustomReportData?.data && !selectedReport) {
                const matchingReport = MasterReportData.data.find(
                    (report: MasterReport) => report.reportName === CustomReportData.data.reportCategory
                );

                if (matchingReport) {
                    handleReportSelect(matchingReport);

                    // Set the selected columns from the custom report data
                    const initialSelection: Record<string, boolean> = {};
                    CustomReportData.data.selectedColumns.forEach((col: string) => {
                        initialSelection[col] = true;
                    });
                    setSelectedColumns(initialSelection);

                    // Set selectAll based on whether all columns are selected
                    const allColumns = matchingReport.reportWiseColumns.All || matchingReport.reportWiseColumns.all || [];
                    const allSelected = allColumns.every((col: any) =>
                        CustomReportData.data.selectedColumns.includes(col.field)
                    );
                    setSelectAll(allSelected);
                }
            }
        }
    }, [MasterReportData, CustomReportData]);

    const processedData = useMemo(() => {
        if (!MasterReportData) return [];

        return (MasterReportData.data || []).map((report: any) => {
            return {
                ...report,
            };
        });
    }, [MasterReportData]);

    const [selectedReport, setSelectedReport] = useState<MasterReport | null>(null);
    const [selectAll, setSelectAll] = useState(false);

    const filteredReports = useMemo(() => {
        if (!searchTerm) {
            return processedData.filter((report: MasterReport) =>
                (report.reportName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    report.reportType.toLowerCase().includes(searchTerm.toLowerCase())) &&
                report.reportName !== "vehicledistancerunreport" &&
                report.reportName !== "dailyevent"
            );
        }

        return processedData.filter((report: MasterReport) =>
            report.reportName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            report.reportType.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [processedData, searchTerm]);

    const reorder = (list: string[], startIndex: number, endIndex: number) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result;
    };
    useEffect(() => {
        if (!selectedReport) return;
        const selected = Object.entries(selectedColumns)
            .filter(([_, isSelected]) => isSelected)
            .map(([field]) => field);
        setSelectedColumnsOrder(prevOrder => [
            ...prevOrder.filter(field => selected.includes(field)),
            ...selected.filter(field => !prevOrder.includes(field))
        ]);
    }, [selectedColumns, selectedReport]);
    const onDragEnd = useCallback((result: any) => {
        if (!result.destination) {
            return;
        }

        if (result.source.index === result.destination.index) {
            return;
        }

        const newOrder = reorder(
            selectedColumnsOrder,
            result.source.index,
            result.destination.index
        );

        setSelectedColumnsOrder(newOrder);
    }, [selectedColumnsOrder]);

    const [isEnabled, setIsEnabled] = useState(false);

    const handleReportSelect = (report: MasterReport) => {
        setSelectedReport(report);
        // Initialize all columns as unchecked
        const initialSelection: Record<string, boolean> = {};
        const columns = report.reportWiseColumns.All || report.reportWiseColumns.all || [];
        columns.forEach((col: any) => {
            initialSelection[col.field] = false;
        });
        setSelectedColumns(initialSelection);
        setSelectAll(false);

        // You might want to set the report name automatically:
        if (!reportName) {
            setReportName(report.reportDisplayName); // Or report.reportName depending on your needs
        }
    };

    const toggleColumn = (columnName: string) => {
        setSelectedColumns(prev => {
            const newSelected = {
                ...prev,
                [columnName]: !prev[columnName]
            };

            // Update the order array when selecting a new column
            if (newSelected[columnName] && !selectedColumnsOrder.includes(columnName)) {
                setSelectedColumnsOrder(prev => [...prev, columnName]);
            }

            return newSelected;
        });
    };

    // Function to toggle select all
    const toggleSelectAll = () => {
        const newSelectAll = !selectAll;
        setSelectAll(newSelectAll);

        if (selectedReport) {
            const columns = selectedReport.reportWiseColumns.All || selectedReport.reportWiseColumns.all || [];
            const updatedSelection: Record<string, boolean> = {};
            columns.forEach((col: any) => {
                updatedSelection[col.field] = newSelectAll;
            });
            setSelectedColumns(updatedSelection);
        }
    };
    const columns = selectedReport?.reportWiseColumns?.All || selectedReport?.reportWiseColumns?.all || [];

    const [columnSearchTerm, setColumnSearchTerm] = useState('');

    const filteredColumns = useMemo(() => {
        if (!columnSearchTerm || !selectedReport) {
            return selectedReport?.reportWiseColumns?.All || selectedReport?.reportWiseColumns?.all || [];
        }

        const columns = selectedReport.reportWiseColumns.All || selectedReport.reportWiseColumns.all || [];
        return columns.filter((column: any) =>
            column.label.toLowerCase().includes(columnSearchTerm.toLowerCase()) ||
            column.field.toLowerCase().includes(columnSearchTerm.toLowerCase())
        );
    }, [columnSearchTerm, selectedReport]);

    const [finalColumnsSearchTerm, setFinalColumnsSearchTerm] = useState('');

    const [isDateRangeModalOpen, setIsDateRangeModalOpen] = useState(false);

    const handleContinue = (e: React.MouseEvent) => {
        e.preventDefault();
        setIsDateRangeModalOpen(true);
    };

    const isContinueDisabled = useMemo(() => {
        return (
            !reportName.trim() ||
            !reportDescription.trim() ||
            Object.values(selectedColumns).filter(Boolean).length === 0
        );
    }, [reportName, reportDescription, selectedColumns]);

    useEffect(() => {
        if (selectedDuration === "Custom Range") {
            const isValid = startDate !== "" && endDate !== "" && dayjs(startDate).isBefore(dayjs(endDate));
            setIsDateRangeValid(isValid);
        } else {
            setIsDateRangeValid(true);
        }
    }, [selectedDuration, startDate, endDate]);

    const {
        data: customReportData,
        error: customReportError,
        loading: customReportLoading,
        fetchData: customReportRequestData,
    } = useApiError<EmailApiResponse>(API_ENDPOINTS.report.CUSTOM_REPORT_EDIT, "POST"
    );

    const handleApplyDateRange = async () => {
        setIsSendingEmail(true);
        if (selectedDuration === "Custom Range" && (!startDate || !endDate || dayjs(startDate).isAfter(dayjs(endDate)))) {
            setIsDateRangeValid(false);
            return;
        }
        if (!selectedReport) {
            console.error("No report selected");
            return;
        }

        const orderedSelectedColumns = selectedColumnsOrder.filter(
            field => selectedColumns[field]
        );

        const requestData = {
            report_id: report_id,
            date_time: CustomReportData?.data?.date_time,
            fleet_id: user.customer_id,
            parent_id: user.parentId,
            reportCategory: selectedReport.reportName,
            reportName: reportName,
            reportDescription: reportDescription,
            selectedColumns: orderedSelectedColumns,
            startDate: selectedDuration === "Custom Range" ? startDate : startDateC,
            endDate: selectedDuration === "Custom Range" ? endDate : endDateC,
            createdAt: CustomReportData?.data?.createdAt,
        };

        try {
            const response = await customReportRequestData(requestData);
            if (response?.status === 201) {
                setAlertMessage(response?.message);
                setAlertType("success");
                setAlertOpen(true);
                setIsDateRangeModalOpen(false);
                setTimeout(() => {
                    navigate(-1);
                }, 2000);
            } else {
                setAlertMessage(response?.error);
                setAlertType("error");
                setAlertOpen(true);
                setIsDateRangeModalOpen(false);
            }
        } catch (error) {
            console.error("Error sending email:", error);
            setAlertMessage((error as Error).message);
            setAlertType("error");
            setAlertOpen(true);
        } finally {
            setIsSendingEmail(false);
        }
    };

    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewData, setPreviewData] = useState<any>(null);

    const handlePreview = () => {
        if (!selectedReport) {
            console.error("No report selected");
            return;
        }

        const orderedSelectedColumns = selectedColumnsOrder.filter(
            field => selectedColumns[field]
        );

        const columns = selectedReport.reportWiseColumns.All || selectedReport.reportWiseColumns.all || [];
        const selectedLabels = orderedSelectedColumns.map(field => {
            const column = columns.find((col: any) => col.field === field);
            return column ? column.label : field;
        });


        const previewData = {
            report_id: report_id,
            date_time: selectedReport.date_time,
            fleet_id: user.customer_id,
            parent_id: user.parentId,
            reportCategory: selectedReport.reportName,
            reportName: reportName,
            reportDescription: reportDescription,
            selectedColumns: selectedLabels,
            startDate: selectedDuration === "Custom Range" ? startDate : startDateC,
            endDate: selectedDuration === "Custom Range" ? endDate : endDateC,
        };

        setPreviewData(previewData);
        setIsPreviewOpen(true);
    };

    if (CustomReportLoading) {
        return <div>Loading...</div>;
    }

    if (CustomReportError) {
        return <div>Error loading report data</div>;
    }

    return (
        <div className='flex flex-col gap-4 w-full'>
            {/* Search Input */}
            <div className="w-full pt-8">
                <button
                    className="text-blue-600 font-medium flex items-center"
                    onClick={handleBackClick}
                >
                    <ArrowBack className="mr-1" /> Back
                </button>
            </div>
            <div className=' w-full flex lg:flex-row md:flex-row flex-col justify-between gap-4 xs:flex-col items-start'>
                <div className="max-w-max">
                    <div className="dashboard-header-left">
                        <div className="flex flex-col items-start">
                            <Typography variant="h4" fontSize="24px" fontWeight={600} lineHeight='36px'>
                                Edit Report Template
                            </Typography>
                        </div>
                    </div>
                </div>

                <div className="flex gap-4 flex-wrap">
                    <CustomIconButton
                        icon={<img src={eyeIcon} alt="preview" width={16} height={16} className={`${isContinueDisabled ? 'opacity-50' : 'opacity-100'}`} />}
                        text="Preview"
                        background="transparent"
                        variant='outlined'
                        onClick={handlePreview}
                        isDisabled={isContinueDisabled}
                    />
                </div>

                {isPreviewOpen && previewData && (
                    <CustomReportPreview
                        data={previewData}
                        onClose={() => setIsPreviewOpen(false)}
                    />
                )}

            </div>
            <form className='w-full relative py-8' onSubmit={(e) => e.preventDefault()}>
                <div className='flex items-start lg:flex-row md:flex-row flex-col gap-4'>
                    <div className='flex gap-4 lg:w-1/3 md:w-1/2 w-full'>
                        <InputField
                            label="Report Name"
                            name="reportName"
                            value={reportName}
                            onChange={(e) => setReportName(e.target.value)}
                            placeholder="Enter Report Name"
                            type="text"
                            isMandatory={true}
                        />
                    </div>

                    <div className="flex flex-col lg:w-2/3 md:w-1/2 w-full">
                        <Typography variant="body2" fontWeight={600} color="textPrimary" className="self-start flex items-center space-x-1">
                            <span>Report Description</span>
                            <span className="text-red-500" title="Required field">*</span>
                        </Typography>

                        <textarea
                            name="reportDescription"
                            value={reportDescription}
                            // onChange={(e) => setReportDescription(e.target.value)}
                            onChange={(e) => {
                                const sanitizedValue = DOMPurify.sanitize(e.target.value, {
                                    ALLOWED_TAGS: [],
                                    ALLOWED_ATTR: [],
                                });
                                setReportDescription(sanitizedValue);
                            }}
                            placeholder="Enter Description"
                            rows={4}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '8px',
                                border: '1px solid #ccc',
                                fontSize: '14px',
                                outline: 'none',
                                resize: 'vertical',
                                marginTop: '8px',
                                color: '#242424',
                            }}
                        />
                    </div>
                </div>
                <div className='grid lg:grid-cols-3 md:grid-cols-3 grid-cols-1 gap-4 mt-4'>
                    <Box className='flex gap-4 flex-col p-4 rounded-lg border border-[#F0EFF8]' sx={{ bgcolor: 'background.paper' }}>
                        <Typography variant="h4" fontSize="24px" fontWeight={600} lineHeight='36px' sx={(theme) => ({
                            color: theme.palette.text.secondary,
                        })}>
                            Pick a Category
                        </Typography>
                        <div className='f-full relative'>
                            <img src={searchIcon} alt='search-icon' className='absolute left-4 top-[45%] z-10' />
                            <InputField
                                name="reportName"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search"
                                type="text"
                                extraClass='pl-10'
                            />
                        </div>
                        <div className='flex flex-col gap-2 mt-4 max-h-[600px] overflow-y-auto'>
                            {filteredReports && filteredReports.map((report: MasterReport) => (
                                <Box
                                    key={report.reportName}
                                    sx={(theme) => ({
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 2,
                                        border: '1px solid',
                                        borderColor: '#F0EFF8',
                                        p: 4,
                                        borderRadius: 2,
                                        cursor: 'pointer',
                                        ...(selectedReport?.reportName === report.reportName && {
                                            borderColor: theme.palette.selected,
                                            backgroundColor: theme.palette.selected
                                        })
                                    })}
                                    onClick={() => handleReportSelect(report)}
                                >
                                    <Typography variant="body2" fontSize="18px" fontWeight={600} lineHeight='28px'>
                                        {report.reportDisplayName}
                                    </Typography>
                                    <Typography variant="body2" fontSize="16px" fontWeight={400} lineHeight='24px'>
                                        {report.reportDescription}
                                    </Typography>
                                </Box>
                            ))}
                        </div>
                    </Box>
                    <Box className='flex gap-4 flex-col p-4 rounded-lg border border-[#F0EFF8]' sx={(theme) => ({
                        bgcolor: theme.palette.background.paper
                    })}>
                        <div className='flex justify-between items-center w-full'>
                            <Typography variant="h4" fontSize="24px" fontWeight={600} lineHeight='36px'>
                                Pick Your Column
                            </Typography>
                            <Typography
                                variant="body2"
                                fontSize="14px"
                                fontWeight={600}
                                lineHeight='26px'
                                color="primary"
                                onClick={toggleSelectAll}
                                style={{ cursor: 'pointer' }}
                            >
                                {selectAll ? 'Deselect All' : 'Select All'}
                            </Typography>
                        </div>
                        <div className='f-full relative'>
                            <img src={searchIcon} alt='search-icon' className='absolute left-4 top-[45%] z-10' />
                            <InputField
                                name="columnSearch"
                                value={columnSearchTerm}
                                onChange={(e) => setColumnSearchTerm(e.target.value)}
                                placeholder="Search Columns"
                                type="text"
                                extraClass='pl-10'
                            />
                        </div>
                        {selectedReport && (selectedReport.reportWiseColumns.All || selectedReport.reportWiseColumns.all) ? (
                            <div className='flex flex-col gap-2 mt-4 max-h-[600px] overflow-y-auto'>
                                {filteredColumns.map((column: any) => (
                                    <div key={column.field} className='flex gap-2 border border-[#F0EFF8] p-4 rounded-lg justify-between'>
                                        <Typography variant="body2" fontSize="16px" fontWeight={500} lineHeight='26px'>
                                            {column.label}
                                        </Typography>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                className="sr-only peer"
                                                checked={selectedColumns[column.field] || false}
                                                onChange={() => toggleColumn(column.field)}
                                            />
                                            <Box className={`w-12 h-6 flex items-center rounded-full transition-colors duration-300 ease-in-out`}
                                                sx={(theme) => ({
                                                    backgroundColor: selectedColumns[column.field] ? theme.palette.primary.main : theme.palette.secondary.main
                                                })}
                                            >
                                                <div className={`bg-white w-5 h-5 rounded-full shadow-md transform transition-transform duration-300 ${selectedColumns[column.field] ? 'translate-x-6' : 'translate-x-1'
                                                    }`}></div>
                                            </Box>
                                        </label>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <Typography variant="body2" className="text-gray-500 mt-4">
                                Select a report from the left to see its columns
                            </Typography>
                        )}
                    </Box>
                    <Box className='flex gap-4 flex-col p-4 rounded-lg border border-[#F0EFF8]' sx={(theme) => ({
                        bgcolor: theme.palette.background.paper
                    })}>
                        <div className='flex justify-between items-center w-full relative'>
                            <div className='flex flex-col gap-4 w-full'>
                                <Typography variant="h4" fontSize="24px" fontWeight={600} lineHeight='36px'>
                                    Manage Final Columns
                                </Typography>
                                <Typography
                                    variant="body2"
                                    fontSize="14px"
                                    fontWeight={400}
                                    lineHeight='26px'
                                    color="secondary"
                                >
                                    Note : The columns will be arranged in this specified order.
                                </Typography>
                            </div>
                            <Typography className='absolute right-0 top-0 rounded-md px-2' variant='body2' fontSize='13px' lineHeight='24px' fontWeight={400} bgcolor='#D9EDE6' color='#219653'>
                                {Object.values(selectedColumns).filter(Boolean).length} Columns
                            </Typography>
                        </div>
                        <div className='f-full relative'>
                            <img src={searchIcon} alt='search-icon' className='absolute left-4 top-[45%] z-10' />
                            <InputField
                                name="finalColumnsSearch"
                                value={finalColumnsSearchTerm}
                                onChange={(e) => setFinalColumnsSearchTerm(e.target.value)}
                                placeholder="Search Columns"
                                type="text"
                                extraClass='pl-10'
                            />
                        </div>
                        {selectedReport ? (
                            <div className='flex flex-col gap-2 mt-4 max-h-[600px] overflow-y-auto'>
                                <DragDropContext onDragEnd={onDragEnd}>
                                    <Droppable droppableId="selectedColumns">
                                        {(provided) => (
                                            <div
                                                {...provided.droppableProps}
                                                ref={provided.innerRef}
                                                className="space-y-2"
                                            >
                                                {selectedColumnsOrder
                                                    .filter(field => selectedColumns[field])
                                                    .filter(field =>
                                                        field.toLowerCase().includes(finalColumnsSearchTerm.toLowerCase())
                                                    )
                                                    .map((field, index) => {
                                                        const column = (selectedReport.reportWiseColumns.All ||
                                                            selectedReport.reportWiseColumns.all)?.find((col: any) => col.field === field);
                                                        return (
                                                            <Draggable key={field} draggableId={field} index={index}>
                                                                {(provided) => (
                                                                    <div
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                        {...provided.dragHandleProps}
                                                                        className='flex gap-2 border border-[#F0EFF8] bg-[#F0EFF8] p-2 rounded-lg justify-between items-center'
                                                                    >
                                                                        <div className="flex items-center gap-2">
                                                                            <img
                                                                                src={dragIcon}
                                                                                alt="drag-handle"
                                                                                className="cursor-move"
                                                                            />
                                                                            <Typography variant="body2" fontSize="16px" fontWeight={500} lineHeight='26px'>
                                                                                {column?.label || field}
                                                                            </Typography>
                                                                        </div>
                                                                        <IconButton
                                                                            onClick={() => toggleColumn(field)}
                                                                            size="small"
                                                                            sx={{
                                                                                color: 'error.main',
                                                                                '&:hover': {
                                                                                    backgroundColor: 'rgba(255, 0, 0, 0.1)'
                                                                                }
                                                                            }}
                                                                        >
                                                                            <img src={deleteIcon} alt='delete-icon' />
                                                                        </IconButton>
                                                                    </div>
                                                                )}
                                                            </Draggable>
                                                        );
                                                    })}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </DragDropContext>

                                {selectedColumnsOrder.filter(field => selectedColumns[field]).length === 0 && (
                                    <Typography variant="body2" className="text-gray-500 mt-4">
                                        No columns selected. Select columns from "Pick Your Column" section.
                                    </Typography>
                                )}
                            </div>
                        ) : (
                            <Typography variant="body2" className="text-gray-500 mt-4">
                                Select a report from the left to see its columns
                            </Typography>
                        )}
                    </Box>
                </div>
                <div className='flex justify-end items-center w-full mt-4 gap-4'>
                    {!isContinueDisabled &&
                        <Buttons
                            text="Save as New Template"
                            onClick={(e) => {
                                e.preventDefault();
                                console.log('data-saved-template');
                            }}
                            disabled={false}
                            fullWidth={false}
                            variant='outlined'
                        />
                    }

                    <Buttons
                        text="Update Report"
                        onClick={handleContinue}
                        disabled={isContinueDisabled}
                        fullWidth={false}
                    />
                </div>
                {isDateRangeModalOpen &&
                    <div className='w-full h-full fixed left-0 right-0 mx-auto top-0 flex z-[999]'>
                        <div className='fixed w-full h-full bg-black opacity-50 top-0 left-0 z-[999]'></div>
                        <Box
                            className='absolute left-0 right-0 mx-auto z-[1000] top-1/2 transform -translate-y-1/2 rounded-xl w-full max-w-[480px] h-[600px] p-4'
                            sx={{
                                width: 480,
                                borderRadius: '12px',
                                bgcolor: 'background.paper',
                                boxShadow: 24,
                                p: 4,
                                overflow: 'hidden'
                            }}
                        >
                            <Typography variant='h4' fontWeight={600} fontSize='24px' lineHeight='36px'>
                                Select Date Range
                            </Typography>
                            <Typography variant='body2' fontWeight={400} color='secondary' fontSize='14px' lineHeight='24px'>
                                Select a date range for which you want to generate your report
                            </Typography>
                            <Typography variant='h4' fontWeight={600} fontSize='14px' lineHeight='26px'>
                                Date Range
                            </Typography>
                            <Box>
                                <div className="flex flex-col gap-4 mt-4">
                                    <div className="grid grid-cols-1 gap-2">
                                        <FormControl component="fieldset" fullWidth>
                                            <RadioGroup
                                                aria-label="date-range"
                                                name="dateRange"
                                                value={selectedDuration}
                                                onChange={(e) => {
                                                    const option = e.target.value;
                                                    setSelectedDuration(option);
                                                    if (option !== "Custom Range") {
                                                        const { startDate: newStartDate, endDate: newEndDate } = getDateRange(option);
                                                        setStartDate(newStartDate ?? '');
                                                        setEndDate(newEndDate ?? '');
                                                    }
                                                }}
                                                sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}
                                            >
                                                {["Last 7 Days", "Last 15 Days", "Last 30 Days", "Custom Range"].map((option) => (
                                                    <FormControlLabel
                                                        key={option}
                                                        value={option}
                                                        control={<Radio />}
                                                        label={option}
                                                        sx={{
                                                            '& .MuiFormControlLabel-label': {
                                                                color: '#5547DD',
                                                                fontWeight: '700',
                                                            },
                                                            margin: 0,
                                                            padding: '8px 12px',
                                                            border: '1px solid #E5E7EB',
                                                            borderRadius: '6px',
                                                            backgroundColor: selectedDuration === option ? '#F3F4F6' : 'transparent',
                                                        }}
                                                    />
                                                ))}
                                            </RadioGroup>
                                        </FormControl>
                                    </div>

                                    {selectedDuration === "Custom Range" && (
                                        <>
                                            <div className="grid grid-cols-1 gap-4 mt-2">
                                                <div className="flex gap-4 sm:flex-row flex-col">
                                                    <div className="flex-1">
                                                        <DateField
                                                            id="fromDate"
                                                            label="From Date"
                                                            name="departureDate"
                                                            value={startDate}
                                                            onChange={(value: string | null) => setStartDate(value ?? '')}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="flex gap-4 sm:flex-row flex-col">
                                                    <div className="flex-1">
                                                        <DateField
                                                            id="toDate"
                                                            label="To Date"
                                                            name="arrivalDate"
                                                            value={endDate}
                                                            onChange={(value: string | null) => setEndDate(value ?? '')}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            {!isDateRangeValid && (
                                                <Typography color="error" variant="body2">
                                                    Please select valid date range (From date must be before To date)
                                                </Typography>
                                            )}
                                        </>
                                    )}

                                    {/* {selectedDuration !== "Custom Range" && (
                                        <div className="mt-4 p-3 bg-blue-50 rounded-md">
                                            <Typography variant="body2">
                                                Selected Range: {startDateC} to {endDateC}
                                            </Typography>
                                        </div>
                                    )} */}
                                </div>
                            </Box>

                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                mt: 2,
                                py: 2,
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                px: 2
                            }}>
                                <Button
                                    onClick={() => setIsDateRangeModalOpen(false)}
                                    variant="outlined"
                                    sx={{ mr: 2 }}
                                >
                                    Back
                                </Button>
                                <Button
                                    onClick={handleApplyDateRange}
                                    variant="contained"
                                    disabled={selectedDuration === "Custom Range" && !isDateRangeValid}
                                >
                                    {isSendingEmail ? "Generating..." : "Generate Report"}
                                </Button>
                            </Box>
                        </Box>
                    </div>
                }

                {/* Back Confirmation Modal - Only shows when there are unsaved changes */}
                {showBackConfirmation && (
                    <Box
                        sx={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1300
                        }}
                        onClick={() => setShowBackConfirmation(false)} // Clicking outside closes the modal
                    >
                        <Box
                            sx={{
                                backgroundColor: 'background.paper',
                                borderRadius: 4,
                                boxShadow: 24,
                                width: '100%',
                                maxWidth: '600px',
                                p: 4
                            }}
                            onClick={(e) => e.stopPropagation()} // Prevent click from bubbling to parent
                        >
                            <Typography variant="h6" component="h2" gutterBottom>
                                Changes Unsaved
                            </Typography>
                            <Typography variant="body2" fontWeight={400} color='secondary' sx={{ mb: 3 }}>
                                You have unsaved changes on this screen. If you leave now, your changes will be lost. Would you like to save them before continuing?
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                <Button
                                    onClick={() => setShowBackConfirmation(false)}
                                    variant="outlined"
                                >
                                    Stay on Page
                                </Button>
                                <Button
                                    onClick={() => {
                                        setShowBackConfirmation(false);
                                        navigate(-1);
                                    }}
                                    variant="contained"
                                    color="error"
                                >
                                    Continue Without Saving
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                )}
                <AlertToast message={alertMessage} type={alertType} open={alertOpen} onClose={() => setAlertOpen(false)} />
            </form>
        </div>
    );
}